import * as React from 'react'
import { castingStoreGetFirstEnabledDateFrom, castingStoreGetMaxRecurrenceEndDate, castingStoreGetMinRecurrenceEndDate, castingStoreGetMultiOpeningHours, castingStoreGetPrescriberPrescriberCastingFundingEndDate, castingStoreGetPrescriberRemainingFundedHours, castingStoreSimulateOccurrencesUpToMaxAllowedDuration, StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { moment } from '../../../../services/utils/moment.service'
import { openableDateToWorkingDay } from '../../../../services/utils/openables.service'
import { formatDurationFromHours } from '../../../../services/utils/utils.service'
import { getDayPickerDateLocale, StyledDayPicker } from '../../../shared/styled-day-picker.component'

const i18nScoped = new I18nService(require('./casting-end-date-picker.component.yml'))

export default function CastingEndDatePickerComponent ({ store, data, data: { slots, offeredService, prescriber } }: StoreProps) {
  const multiOpeningHoursUnsafeMidnight = React.useMemo(() => {
    if (!slots.length || !slots[0].start_date) return null
    return castingStoreGetMultiOpeningHours(data).multiOpeningHoursUnsafeMidnight
  }, [data, slots])

  const isDayDisabled = React.useCallback((date: Date) => {
    // when groups are enabled, workings days and openning hours are not relevant (group_session are the single source of truth)
    return !offeredService.enable_groups && !multiOpeningHoursUnsafeMidnight?.[openableDateToWorkingDay(date)].length
  }, [multiOpeningHoursUnsafeMidnight, offeredService.enable_groups])

  const setEndDate = React.useCallback((endDate: Date | null) => {
    const date = endDate ? moment(endDate).format('YYYY-MM-DD') : null
    store.setSlots(slots.map(slot => ({ ...slot, end_date: date })))
    if (endDate || !offeredService.enable_groups) store.nextFocus({ closeOnMobile: true })
  }, [offeredService.enable_groups, slots, store])

  const infoMessages = React.useMemo(() => {
    const messages: string[] = []
    if (!prescriber || !slots.length || !slots[0].start_date) return messages

    const remainingFundedHours = castingStoreGetPrescriberRemainingFundedHours(data)
    if (typeof remainingFundedHours === 'number') {
      messages.push(i18nScoped.t('prescriber_fund_up_to_x_hours', {
        prescriber_name: prescriber.name,
        hours: formatDurationFromHours(remainingFundedHours),
        occurrences: castingStoreSimulateOccurrencesUpToMaxAllowedDuration(data, remainingFundedHours, { end_date: null }).length
      }))
    }

    const prescriberCastingFundingEndDate = castingStoreGetPrescriberPrescriberCastingFundingEndDate(data)
    if (prescriberCastingFundingEndDate) {
      messages.push(i18nScoped.t('prescriber_plans_ends_at', { date: prescriberCastingFundingEndDate.format('ll') }))
    }

    return messages
  }, [data, prescriber, slots])

  if (!slots.length || !slots[0].start_date) return null
  
  const minEndDate = castingStoreGetMinRecurrenceEndDate(data)
  const maxEndDate = castingStoreGetMaxRecurrenceEndDate(data)
  const initialMonth = castingStoreGetFirstEnabledDateFrom(minEndDate.toDate(), isDayDisabled)
  const endDate = slots.length > 0 && slots[0].end_date ? moment(slots[0].end_date) : null

  return <>
    { infoMessages.length > 0 && <ul style={{ marginBottom: '1.5em' }}>{ infoMessages.map((msg, index) => <li key={ index }>{ msg }</li>) }</ul> }

    { !offeredService.enable_groups && <div className="inputRadio" style={{ marginBottom: '0.5em' }}>
      <input type="checkbox" id="no_end_date" onChange={ () => setEndDate(null) } checked={ endDate === null } />
      <label htmlFor="no_end_date">{ i18nScoped.t(maxEndDate ? 'until_max_end_date' : 'no_end_date') }</label>
    </div> }

    <StyledDayPicker
      mode='single'
      defaultMonth={ endDate?.toDate() || initialMonth }
      startMonth={ initialMonth }
      locale={ getDayPickerDateLocale() }
      selected={ endDate?.toDate() }
      disabled={ date => minEndDate.isAfter(date, 'day') || maxEndDate?.isBefore(date, 'day') || isDayDisabled(date) }
      onSelect={ setEndDate } />
  </>
}
